import { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { v4 as uuidv4 } from 'uuid';

import CustomHttpClient from '../customHttpClient';

import { getError } from '../../utils';
import userHelper from '../../utils/userHelper';

import {
  Accessory,
  Base,
  BeneficiaryLife,
  BeneficiaryLifeRequest,
  Brand,
  BrandsByYearResponse,
  CalculateFinalPrizeRequestParams,
  CalculateFinalPrizeServiceResponse,
  CarAccessoriesListResponse,
  CarQuotationServiceRequest,
  CarQuotationServiceRequestParams,
  CarQuotationServiceResponse,
  CarReQuotationServiceRequest,
  CarReQuotationServiceRequestParams,
  CarReQuotationServiceResponse,
  Combo,
  CommerceQuotationRequest,
  CommerceQuotationRequestParams,
  CommerceQuotationResponse,
  ConsortiumQuotationRequest,
  ConsortiumQuotationRequestParams,
  ConsortiumQuotationResponse,
  CoveragePerPlan,
  CurrencyCode,
  DateFromMandatoryLife,
  DeleteInsuredObjectParams,
  DeleteInsuredObjectRequest,
  EmissionRightLifeObligatory,
  FeaturesVariousRisksRequest,
  FeaturesVariousRisksService,
  GenerateAPQuotationServiceRequest,
  GenerateAPQuotationServiceResponse,
  GenerateNewQuotationServiceRequest,
  GenerateNewQuotationServiceResponse,
  GetActivitiesPerProductResponse,
  GetAgeRangeListServiceResponse,
  GetArticleGroupsResponse,
  GetClientListResponse,
  GetCommertialsConditionsResponse,
  GetCoveragesPerPlanActivityServiceResponse,
  GetCoveragesPerPlanServiceResponse,
  GetIncreaseAndDecreaseOfEPVServiceResponse,
  GetListCollateralCreditorsServiceResponse,
  GetPaymentPlansServiceResponse,
  GetPeopleTypeListPerArticleResponse,
  GetProductsListPerProducerResponse,
  GetQuotationGraphicInfoServiceResponse,
  GetRelationshipListPerProductServiceResponse,
  GetTrackersListServiceResponse,
  HomeQuotationRequest,
  HomeQuotationResponse,
  InsertInsuredObjectParams,
  InsertInsuredObjectRequest,
  InsertInsuredPayrollLife,
  InsertInsuredPolicyholder,
  InsertRiskLocationRequest,
  InsertVehicleDataServiceRequest,
  InsertVehicleDataServiceRequestParams,
  InspectionTypes,
  Insured,
  InsuredResponse,
  LifeSumObligatory,
  ListCarInspectionTypesResponse,
  Model,
  ModelByBrandAndYearResponse,
  OperationTypes,
  RegenerateAPQuotationServiceResponse,
  RemoveVehicleDataServiceRequest,
  RemoveVehicleDataServiceRequestParams,
  RequestCoveragesByPlanActivities,
  RequestPlansByProducer,
  SavedQuotesService,
  SaveQuotationServiceRequest,
  SaveQuotationServiceResponse,
  SelectCoveragePerVehicleServiceRequest,
  SendProposalRequest,
  TypesHousingResponse,
  UserInformation,
  ValidateFeaturesRiskVarious,
  VersionByYearMarkModelResponse,
  WebCommerceActivities,
  YearResponse,
  Years,
} from '../../interfaces/services';
import { CarScoringQuestionsResponse } from '../../interfaces/services/get-car-scoring-questions-response';
import {
  CivilQuotationRequest,
  CivilQuotationRequestParams,
  CivilQuotationResponse,
} from '../../interfaces/services/civil-quotation-service';
import {
  FireQuotationRequest,
  FireQuotationRequestParams,
  FireQuotationResponse,
} from '../../interfaces/services/fire-quotation-service';
import { GetPlansByProducerAndActivityResponse } from '../../interfaces/services/get-plans-by-producer-and-activity-service';
import { InsertPaymentData } from '../../interfaces/services/insert-payment-data-request-service';
import {
  PersonalEffectsQuotationRequest,
  PersonalEffectsQuotationRequestParams,
  PersonalEffectsQuotationResponse,
} from '../../interfaces/services/personal-effects-quotation-service';
import { SelectOption } from '../../interfaces';
import {
  TheftInsuranceQuotationRequest,
  TheftInsuranceQuotationRequestParams,
  TheftInsuranceQuotationResponse,
} from '../../interfaces/services/theft-insurance-quotation-service';
import {
  WebDiscountRateRequest,
  WebDiscountRateResponse,
} from '../../interfaces/services/get-car-web-discount-rate-response-service';

import { LoadSavedQuotations, QuotationGroups } from '../../../pages/quotation/NewQuote/containers';
import { RenewQuotationServiceRequest } from '../../interfaces/services/renew-quotation-service-request';
import { LoadQuotationToRenewResponse } from '../../../pages/quotation/NewQuote/containers/shared/interfaces/load-renewal-quotation-response-interface';
import { Representative, RepresentativeTypes } from '../../interfaces/services/legal-representative-types-service';
import { LegalRepresentative, PostInsertRepresentativeLegal } from '../../interfaces/services/insert-legal-representative-service';
import { QuotePdfServiceRequest, QuotePdfServiceResponse } from '../../interfaces/services/quote-pdf-service';
import { RequestListTextSubcategory } from '../../interfaces/services/request-list-text-subcategory';
import { GetListTextSubcategoryResponse } from '../../interfaces/services/get-list-text-subcategory';
import { TransferRightsRequest } from '../../interfaces/services/transfer-rights-service';

export const DEFAULT_PAYMENT_PLAN_CODE = '1';

const getUserInfo = () => {
  const {
    codigoDeEmpresa,
    codigoDeSucursal,
    intermediarioPrincipal,
    intermediarioSecundario,
  }: UserInformation = userHelper.getUser()!;

  return {
    empresa: codigoDeEmpresa,
    sucursal: codigoDeSucursal,
    nivt: intermediarioSecundario?.nivelInter,
    nivc: intermediarioSecundario?.codigoInter,
    nit1: intermediarioPrincipal?.nivelInter,
    niv1: intermediarioPrincipal?.codigoInter,
  };
};

const customHttpClientDocuments = CustomHttpClient({
  baseUrl: 'productores/documents',
});

const customHttpClient = CustomHttpClient({
  baseUrl: 'productores/cotizacion',
});

const customHttpClientRenewal = CustomHttpClient({
  baseUrl: 'cotizacion/renovacion',
});

const customHttpClientBase = CustomHttpClient({
  baseUrl: 'productores',
});

const QuotationService = {
  async getPeopleTypeListPerArticle(
    article: string
  ): Promise<AxiosResponse<GetPeopleTypeListPerArticleResponse, any>> {
    const url = `obtenerTiposDePersonaPorArticulo?articulo=${article}`;

    try {
      const response =
        await customHttpClient.get<GetPeopleTypeListPerArticleResponse>(url);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getAPActivitiesPerProduct(
    article: string,
    branch: string,
    product: string
  ): Promise<AxiosResponse<GetActivitiesPerProductResponse, any>> {
    const url = 'actividadApPorProducto';
    const params = {
      ...getUserInfo(),
      articulo: article,
      rama: branch,
      producto: product,
    };

    try {
      const response =
        await customHttpClient.get<GetActivitiesPerProductResponse>(url, {
          params,
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getClientList(): Promise<AxiosResponse<GetClientListResponse, any>> {
    const url = 'listaClientesCotizador';
    const params = { ...getUserInfo() };

    try {
      const response = await customHttpClient.get<GetClientListResponse>(url, {
        params,
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getProductsListPerProducer(
    article: string,
    branch: string
  ): Promise<AxiosResponse<GetProductsListPerProducerResponse, any>> {
    const url = 'planesPorProductor';
    const params = {
      ...getUserInfo(),
      rama: branch,
      articulo: article,
    };

    try {
      const response =
        await customHttpClient.get<GetProductsListPerProducerResponse>(url, {
          params,
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPaymentPlans(
    article: string,
    vigencyFrom: string,
    vigencyTo: string
  ): Promise<AxiosResponse<GetPaymentPlansServiceResponse, any>> {
    const url = 'planesDePagoPorFechaDeVigencia';
    const params = {
      articulo: article,
      vigenciaDesde: vigencyFrom,
      vigenciaHasta: vigencyTo,
    };

    try {
      const response =
        await customHttpClient.get<GetPaymentPlansServiceResponse>(url, {
          params,
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getAgeRangeList(
    article: string,
    branch: string,
    product: string
  ): Promise<AxiosResponse<GetAgeRangeListServiceResponse, any>> {
    const url = 'listaRangosDeEdadPorProducto';
    const params = {
      articulo: article,
      rama: branch,
      producto: product,
    };

    try {
      const response =
        await customHttpClient.get<GetAgeRangeListServiceResponse>(url, {
          params,
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getCoveragesPerPlan(
    branch: string,
    product: string,
    currencyCode: string = CurrencyCode.PESOS_ARGENTINOS
  ): Promise<AxiosResponse<GetCoveragesPerPlanServiceResponse, any>> {
    const url = 'coberturasPorPlan';
    const params = {
      codigoMoneda: currencyCode,
      rama: branch,
      producto: product,
    };

    try {
      const response =
        await customHttpClient.get<GetCoveragesPerPlanServiceResponse>(url, {
          params,
        });

      //Generate an ID if not found...
      if (response.status === 200) {
        response.data.coberturas.map((el: CoveragePerPlan) => {
          el.id = el.id || `${el.codigo}-${el.codigoRiesgo}`;
          return el;
        });
      }

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async generateNewQuotation(
    article: string
  ): Promise<AxiosResponse<GenerateNewQuotationServiceResponse, any>> {
    const url = 'nuevaCotizacion';
    const params: GenerateNewQuotationServiceRequest = {
      articulo: article,
      base: getUserInfo(),
      codigoMoneda: CurrencyCode.PESOS_ARGENTINOS,
      tipoOperacion: OperationTypes.TIPO_OPERACION,
      subTipoOperacion: OperationTypes.SUBTIPO_OPERACION,
      subTipoOperacionSistema: OperationTypes.SUBTIPO_OPERACION_SISTEMA,
      superpolizaAfectada: OperationTypes.SUPERPOLIZA_AFECTADA,
    };

    try {
      const response =
        await customHttpClient.post<GenerateNewQuotationServiceResponse>(
          url,
          params
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async generateAPQuotation(
    quotationRequest: GenerateAPQuotationServiceRequest
  ): Promise<AxiosResponse<GenerateAPQuotationServiceResponse, any>> {
    const url = 'cotizarAccidentesPersonales';
    const base: Base = getUserInfo();    
    const request: GenerateAPQuotationServiceRequest = {
      ...quotationRequest,
      base,
      planDePago: DEFAULT_PAYMENT_PLAN_CODE,
    };

    try {
      const response =
        await customHttpClient.post<GenerateAPQuotationServiceResponse>(
          url,
          request
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async regenerateAPQuotation(
    quotationRequest: GenerateAPQuotationServiceRequest
  ): Promise<AxiosResponse<RegenerateAPQuotationServiceResponse, any>> {
    const url = 'reCotizarAccidentesPersonales';
    const base: Base = getUserInfo();
    const request: GenerateAPQuotationServiceRequest = {
      ...quotationRequest,
      base,
    };

    try {
      const response =
        await customHttpClient.post<RegenerateAPQuotationServiceResponse>(
          url,
          request
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async saveQuotation(
    quotationRequest: SaveQuotationServiceRequest
  ): Promise<AxiosResponse<SaveQuotationServiceResponse, any>> {
    const url = 'guardarCotizacion';
    const params: SaveQuotationServiceRequest = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response =
        await customHttpClient.post<SaveQuotationServiceResponse>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getArticleGroups(): Promise<
    AxiosResponse<GetArticleGroupsResponse, any>
  > {
    const url = 'articulos/obtenerGruposDeArticulos';
    const params = { ...getUserInfo() };

    try {
      const response = await customHttpClient.get<GetArticleGroupsResponse>(
        url,
        { params }
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getRelationshipListPerProduct(
    article: string,
    branch: string,
    product: string
  ): Promise<AxiosResponse<GetRelationshipListPerProductServiceResponse, any>> {
    const url = 'listaParentescosPorProducto';
    const params = {
      articulo: article,
      rama: branch,
      producto: product,
    };

    try {
      const response =
        await customHttpClient.get<GetRelationshipListPerProductServiceResponse>(
          url,
          { params }
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getIncreaseAndDecreaseOfEPV(
    branch: string
  ): Promise<AxiosResponse<GetIncreaseAndDecreaseOfEPVServiceResponse, any>> {
    const url = 'recuperarTopesExtraPrimaVariable';
    const params = {
      ...getUserInfo(),
      rama: branch,
    };

    try {
      const response =
        await customHttpClient.get<GetIncreaseAndDecreaseOfEPVServiceResponse>(
          url,
          { params }
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postSendProposal(
    quotationRequest: SendProposalRequest
  ): Promise<AxiosResponse<any, any>> {
    const url = 'enviarPropuesta';
    const params: SaveQuotationServiceRequest = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<any>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postInsertInsuredPolicyholder(
    quotationRequest: InsertInsuredPolicyholder
  ): Promise<AxiosResponse<any, any>> {
    const url = 'insertarAseguradoTomador';
    const params = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<any>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postInsertPaymentData(
    quotationRequest: InsertPaymentData
  ): Promise<AxiosResponse<any, any>> {
    const url = 'insertarDatosDePago';
    const params = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<any>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postInsertInsuredPayrollLife(
    quotationRequest: InsertInsuredPayrollLife
  ): Promise<AxiosResponse<any, any>> {
    const url = 'insertarAseguradoNominaVida';
    const params = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<any>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postDeleteInsuredPayrollLife(
    quotationRequest: any
  ): Promise<AxiosResponse<void>> {
    const url = 'eliminarAseguradoVida';
    const params = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<void>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postInsertBeneficiaryLife(
    beneficiaryLife: BeneficiaryLife
  ): Promise<AxiosResponse<any, any>> {
    const url = 'InsertarBeneficiarioVida';
    const params = {
      ...beneficiaryLife,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<any>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async removeBeneficiaryLife(
    beneficiaryLife: BeneficiaryLifeRequest
  ): Promise<AxiosResponse<any, any>> {
    const url = 'eliminarBeneficiarioVida';
    const params = {
      ...beneficiaryLife,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<any>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getFromDateMandatoryLife(
    date: string
  ): Promise<AxiosResponse<DateFromMandatoryLife, any>> {
    const url = 'obtenerFechaDesdeVidaObligatorio';
    const params = { fecha: date };

    try {
      const response = await customHttpClient.get<DateFromMandatoryLife>(url, {
        params,
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getLifeSumObligatory(): Promise<AxiosResponse<LifeSumObligatory, any>> {
    const url = 'obtenerSumaAseguradaVidaObligatorio';

    try {
      const response = await customHttpClient.get<LifeSumObligatory>(url);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getEmissionRightLifeObligatory(): Promise<
    AxiosResponse<EmissionRightLifeObligatory, any>
  > {
    const url = 'derechoDeEmisionVidaObligatorio';

    try {
      const response = await customHttpClient.get<EmissionRightLifeObligatory>(
        url
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postQuotationLifeObligatory(
    quotationRequest: any
  ): Promise<AxiosResponse<any>> {
    const url = 'cotizarVidaObligatorio';
    const params = {
      base: getUserInfo(),
      ...quotationRequest,
    };

    try {
      const response = await customHttpClient.post<any>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postInsertInsuredLifeObligatory(
    insured: Insured
  ): Promise<AxiosResponse<InsuredResponse>> {
    const url = 'insertarAseguradoVidaObligatorio';
    const params = {
      ...insured,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<InsuredResponse>(
        url,
        params
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getWebCommerceActivities(
    article: string,
    branch: string,
    arse: string
  ): Promise<AxiosResponse<WebCommerceActivities, any>> {
    const url = 'obtenerActividadesComercioWeb';
    const params = {
      ...getUserInfo(),
      articulo: article,
      rama: branch,
      arse,
    };

    try {
      const response = await customHttpClient.get<WebCommerceActivities>(url, {
        params,
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postFeaturesVariousRisks(
    quotationRequest: FeaturesVariousRisksRequest
  ): Promise<AxiosResponse<FeaturesVariousRisksService>> {
    const url = 'obtenerCaracteristicasRiesgosVariosV2';
    const params = {
      ...quotationRequest,
      base: getUserInfo(),
    };    
    try {
      const response = await customHttpClient.post<FeaturesVariousRisksService>(
        url,
        params
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPlansByProducerAndActivity(
    requestParams: RequestPlansByProducer
  ): Promise<AxiosResponse<GetPlansByProducerAndActivityResponse, any>> {
    const url = 'planesPorProductorActividad';
    const params = {
      ...getUserInfo(),
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.get<GetPlansByProducerAndActivityResponse>(url, {
          params,
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getCoveragesByPlanActivity(
    requestParams: RequestCoveragesByPlanActivities
  ): Promise<AxiosResponse<GetCoveragesPerPlanActivityServiceResponse, any>> {
    const url = 'obtenerCoberturasPorPlanActividad';
    const params = {
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.get<GetCoveragesPerPlanActivityServiceResponse>(
          url,
          { params }
        );

      //Generate an ID if not found...
      if (response.status === 200) {
        response.data.coberturas.map((el: any) => {
          el.id = el.id || uuidv4();
          return el;
        });
      }

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postValidateFeaturesRiskVarious(
    quotationRequest: ValidateFeaturesRiskVarious
  ): Promise<AxiosResponse<any>> {
    const url = 'validarCaracteristicasRiesgosVarios/V1';
    const params = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<any>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postInsertRiskLocation(
    quotationRequest: InsertRiskLocationRequest
  ): Promise<AxiosResponse<any>> {
    const url = 'insertarUbicacionDeRiesgo';
    const params = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<any>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async generateFireQuotation(
    fireQuotationRequest: FireQuotationRequest
  ): Promise<AxiosResponse<FireQuotationResponse>> {
    const url = 'cotizarIncendio';
    const params: FireQuotationRequestParams = {
      base: getUserInfo(),
      ...fireQuotationRequest,
    };

    try {
      const response = await customHttpClient.post<FireQuotationResponse>(
        url,
        params
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async generateCommerceQuotation(
    commerceQuotationRequest: CommerceQuotationRequest
  ): Promise<AxiosResponse<CommerceQuotationResponse>> {
    const url = 'cotizarComercio';
    const params: CommerceQuotationRequestParams = {
      ...commerceQuotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<CommerceQuotationResponse>(
        url,
        params
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getComertialsConditions(
    branch: string
  ): Promise<AxiosResponse<GetCommertialsConditionsResponse>> {
    const url = 'recuperarCondicionesComerciales';
    const params = { ...getUserInfo(), rama: branch };

    try {
      const response =
        await customHttpClient.get<GetCommertialsConditionsResponse>(url, {
          params,
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async generateConsortiumQuotation(
    consortiumQuotationRequest: ConsortiumQuotationRequest
  ): Promise<AxiosResponse<ConsortiumQuotationResponse>> {
    const url = 'cotizarConsorcio';
    const params: ConsortiumQuotationRequestParams = {
      base: getUserInfo(),
      ...consortiumQuotationRequest,
    };

    try {
      const response = await customHttpClient.post<ConsortiumQuotationResponse>(
        url,
        params
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async generateCivilQuotation(
    civilQuotationRequest: CivilQuotationRequest
  ): Promise<AxiosResponse<CivilQuotationResponse>> {
    const url = 'cotizarResponsabilidadCivil';
    const params: CivilQuotationRequestParams = {
      base: getUserInfo(),
      ...civilQuotationRequest,
    };

    try {
      const response = await customHttpClient.post<CivilQuotationResponse>(
        url,
        params
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async generatePersonalEffectsQuotation(
    personalEffectsQuotationRequest: PersonalEffectsQuotationRequest
  ) {
    const url = 'cotizarRv';
    const params: PersonalEffectsQuotationRequestParams = {
      base: getUserInfo(),
      ...personalEffectsQuotationRequest,
    };

    try {
      const response =
        await customHttpClient.post<PersonalEffectsQuotationResponse>(
          url,
          params
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getTypesHousing(
    branch: string,
    plan: string
  ): Promise<AxiosResponse<TypesHousingResponse, any>> {
    const url = 'obtenerTiposDeVivienda';
    const params = {
      rama: branch,
      plan,
    };

    try {
      const response = await customHttpClient.get<TypesHousingResponse>(url, {
        params,
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postGenerateHomeQuotation(
    homeQuotationRequest: HomeQuotationRequest
  ): Promise<AxiosResponse<HomeQuotationResponse>> {
    const url = 'cotizarHogar';

    const params: CommerceQuotationRequestParams = {
      ...homeQuotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<HomeQuotationResponse>(
        url,
        params
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postGenerateTheftInsuranceQuotation(
    theftInsuranceQuotationRequest: TheftInsuranceQuotationRequest
  ) {
    const url = 'cotizarRobo';
    const params: TheftInsuranceQuotationRequestParams = {
      base: getUserInfo(),
      ...theftInsuranceQuotationRequest,
    };
    try {
      const response =
        await customHttpClient.post<TheftInsuranceQuotationResponse>(
          url,
          params
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postInsertInsuredObject(object: InsertInsuredObjectRequest) {
    const url = 'insertarObjetoAsegurado';
    const params: InsertInsuredObjectParams = {
      base: getUserInfo(),
      ...object,
    };

    try {
      // TODO Tengo que tipar cuando logre funcionar el servicio.
      const response = await customHttpClient.post<any>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getYearForCombo(): Promise<Combo[]> {
    const url = 'obtenerAnios';
    const params = { ...getUserInfo() };

    try {
      const response = await customHttpClient.get<YearResponse>(url, {
        params,
      });
      const comboData = response.data.anios.map((item: Years) => {
        return {
          value: item.anio,
          label: item.anio,
        };
      });

      return comboData;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getBrandsByYearForCombo(year: string): Promise<Array<SelectOption>> {
    const url = `obtenerMarcasPorAnio?anio=${year}`;
    const params = { ...getUserInfo() };

    try {
      const response = await customHttpClient.get<BrandsByYearResponse>(url, {
        params,
      });
      const comboData = response.data.marcas.map((item: Brand) => {
        return {
          value: item.codigo,
          label: item.descripcion,
        };
      });

      return comboData;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getModelByYearAndBrandCombo(
    year: string,
    mark: string
  ): Promise<Array<SelectOption>> {
    const url = `obtenerModelosPorAnioMarca?anio=${year}&codigoMarca=${mark}`;
    const params = { ...getUserInfo() };

    try {
      const response = await customHttpClient.get<ModelByBrandAndYearResponse>(
        url,
        { params }
      );
      const comboData = response.data.modelos.map((item: Model) => {
        return {
          value: item.codigo,
          label: item.descripcion,
        };
      });

      return comboData;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getVersionByYearAndMarkAndModelCombo(
    year: string,
    mark: string,
    model: string
  ): Promise<AxiosResponse<VersionByYearMarkModelResponse, any>> {
    const url = `obtenerVersionPorAnioMarcaModelo?anio=${year}&codigoMarca=${mark}&codigoModelo=${model}`;
    const params = { ...getUserInfo() };

    try {
      const response =
        await customHttpClient.get<VersionByYearMarkModelResponse>(url, {
          params,
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getCarAccessoriesList(): Promise<Combo[]> {
    const url = 'listaAccesoriosAutos';
    const params = { ...getUserInfo() };

    try {
      const response = await customHttpClient.get<CarAccessoriesListResponse>(
        url,
        { params }
      );
      const comboData = response.data.accesorios.map((item: Accessory) => {
        return {
          value: item.descripcion,
          label: item.descripcion,
        };
      });

      return comboData;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getListCarInspectionTypes(): Promise<Combo[]> {
    const url = 'listaTiposDeInspeccionAutos';
    const params = { ...getUserInfo() };

    try {
      const response =
        await customHttpClient.get<ListCarInspectionTypesResponse>(url, {
          params,
        });
      const comboData = response.data.tiposInspeccion.map(
        (item: InspectionTypes) => {
          return {
            value: item.codigo,
            label: item.descripcion,
          };
        }
      );

      return comboData;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postDeleteInsuredObject(object: DeleteInsuredObjectRequest) {
    const url = 'eliminarObjetoAsegurado';
    const params: DeleteInsuredObjectParams = {
      base: getUserInfo(),
      ...object,
    };

    try {
      const response = await customHttpClient.post<void>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getCarScoringQuestions() {
    const url = 'obtenerPreguntasScoring';

    try {
      const response = await customHttpClient.get<CarScoringQuestionsResponse>(
        url
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getWebDiscountRate(object: WebDiscountRateRequest) {
    const url = 'obtenerTipoDeDescuentoWeb';
    const params = { ...getUserInfo(), ...object };

    try {
      const response = await customHttpClient.get<WebDiscountRateResponse>(
        url,
        { params }
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getQuotationGraphicInfo(
    article: string,
    articleGroup: string
  ): Promise<AxiosResponse<GetQuotationGraphicInfoServiceResponse>> {
    const url = 'graficosCotizador';
    const params = {
      ...getUserInfo(),
      grupoArticulo: articleGroup,
      articulo: article,
    };

    try {
      const response =
        await customHttpClient.get<GetQuotationGraphicInfoServiceResponse>(
          url,
          { params }
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getTrackersList(): Promise<
    AxiosResponse<GetTrackersListServiceResponse>
  > {
    const url = 'listaRastreadores';
    const params = { ...getUserInfo() };

    try {
      const response =
        await customHttpClient.get<GetTrackersListServiceResponse>(url, {
          params,
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async generateCarQuotation(
    requestParams: CarQuotationServiceRequest
  ): Promise<CarQuotationServiceResponse> {
    const url = 'cotizarAutos';
    const params: CarQuotationServiceRequestParams = {
      base: getUserInfo(),
      ...requestParams,
    };

    try {
      const response = await customHttpClient.post<CarQuotationServiceResponse>(
        url,
        params
      );

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async generateCarReQuotation(
    requestParams: CarReQuotationServiceRequest
  ): Promise<CarReQuotationServiceResponse> {
    const url = 'reCotizarAutos';
    const params: CarReQuotationServiceRequestParams = {
      base: getUserInfo(),
      ...requestParams,
    };    
    try {
      const response =
        await customHttpClient.post<CarReQuotationServiceResponse>(url, params);      
        return response.data;
    } catch (error) {      
      throw new Error(getError(error));
    }
  },

  async insertVehicleData(
    requestParams: InsertVehicleDataServiceRequest
  ): Promise<AxiosResponse<any>> {
    const url = 'insertarDatosVehiculo';
    const params: InsertVehicleDataServiceRequestParams = {
      base: getUserInfo(),
      ...requestParams,
    };

    try {
      const response = await customHttpClient.post(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async removeVehicleData(
    requestParams: RemoveVehicleDataServiceRequest
  ): Promise<AxiosResponse<any>> {
    const url = 'eliminaVehiculo';
    const params: RemoveVehicleDataServiceRequestParams = {
      base: getUserInfo(),
      ...requestParams,
    };

    try {
      const response = await customHttpClient.post(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getListOfCollateralCreditors(): Promise<Array<SelectOption>> {
    const url = 'listaAcreedoresPrendarios';

    try {
      const response =
        await customHttpClient.get<GetListCollateralCreditorsServiceResponse>(
          url
        );
      const list = response.data.acreedores.map((el) => ({
        label: el.descripcion,
        value: String(el.codigo),
      }));

      return list;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async selectCoveragePerVehicle(
    requestParams: SelectCoveragePerVehicleServiceRequest
  ): Promise<AxiosResponse<any>> {
    const url = 'seleccionarCoberturaAutos';
    const params = {
      base: getUserInfo(),
      ...requestParams,
    };

    try {
      const response = await customHttpClient.post(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async calculateFinalPrize(
    requestParams: CalculateFinalPrizeRequestParams
  ): Promise<AxiosResponse<CalculateFinalPrizeServiceResponse>> {
    const url = 'calculaPremioFinal';
    const params = {
      base: getUserInfo(),
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.post<CalculateFinalPrizeServiceResponse>(
          url,
          params
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getSavedQuotes(): Promise<AxiosResponse<SavedQuotesService>> {
    const url = 'cabeceraCotizacion';
    const params = { ...getUserInfo() };

    try {
      const response = await customHttpClient.get<SavedQuotesService>(url, {
        params,
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getSavedQuote(quotationNumber: string): Promise<AxiosResponse<LoadSavedQuotations>> {
    const url = 'retomaCotizacion';
    const params = { ...getUserInfo(), numeroCotizacion: quotationNumber };

    try {
      const response = await customHttpClient.get<LoadSavedQuotations>(url, {
        params,
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postRenewalCarInformation(quotationRequest: RenewQuotationServiceRequest): Promise<LoadQuotationToRenewResponse> {
    const url = 'renovacionAutosWeb';
    const body = { 
      base: getUserInfo(),
      tipoDeOperacion: 2,
      subtipoDeOperacion: 0,
      subtipoDeOperacionDelSistema: 0,
      ...quotationRequest
    };    
    try {
      const response = await customHttpClientRenewal.post<LoadSavedQuotations>(url, body);

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postRenewalCommerceInformation(
    quotationRequest: RenewQuotationServiceRequest
  ): Promise<LoadQuotationToRenewResponse> {
    const url = 'renovacionComercio';
    const body: RenewQuotationServiceRequest = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<LoadQuotationToRenewResponse>(url, body);

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postRenewalConsortiumInformation(
    quotationRequest: RenewQuotationServiceRequest
  ): Promise<LoadQuotationToRenewResponse> {
    const url = 'renovacionConsorcio';
    const body: RenewQuotationServiceRequest = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<LoadQuotationToRenewResponse>(url, body);

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postRenewalFamilyInformation(
    quotationRequest: RenewQuotationServiceRequest
  ): Promise<LoadQuotationToRenewResponse> {
    const url = 'renovacionHogar';
    const body: RenewQuotationServiceRequest = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<LoadQuotationToRenewResponse>(url, body);

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postRenewalCRInformation(
    quotationRequest: RenewQuotationServiceRequest
  ): Promise<LoadQuotationToRenewResponse> {
    const url = 'renovacionRCE';
    const body: RenewQuotationServiceRequest = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<LoadQuotationToRenewResponse>(url, body);

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postRenewalPersonalEffectsInformation(
    quotationRequest: RenewQuotationServiceRequest
  ): Promise<LoadQuotationToRenewResponse> {
    const url = 'renovacionRV';
    const body: RenewQuotationServiceRequest = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<LoadQuotationToRenewResponse>(url, body);

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postRenewalTheftInformation(
    quotationRequest: RenewQuotationServiceRequest
  ): Promise<LoadQuotationToRenewResponse> {
    const url = 'renovacionRobo';
    const body: RenewQuotationServiceRequest = {
      base: getUserInfo(),
      ...quotationRequest,
    };

    try {
      const response = await customHttpClient.post<LoadQuotationToRenewResponse>(url, body);

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
  async postRenewalFireInformation(
    quotationRequest: RenewQuotationServiceRequest
  ): Promise<LoadQuotationToRenewResponse> {
    const url = 'renovacionIncendio';
    const body: RenewQuotationServiceRequest = {
      ...quotationRequest,
      base: getUserInfo(),
    };

    try {
      const response = await customHttpClient.post<LoadQuotationToRenewResponse>(url, body);

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getLegalRepresentativeTypes(): Promise<Array<SelectOption>> {
    const url = 'listaTiposRepresentantes';
    const params = { ...getUserInfo() }

    try {
      const response =
        await customHttpClientBase.get(
          url, { params }
        );
      const list = response.data.tiposRepresentantes.Representante.map((el: Representative) => ({
        label: el.DescTipoRepresentante,
        value: String(el.CodTipRepresentante),
      }));

      return list;
      } catch (error) {
      throw new Error(getError(error));
    }
  },
  
  async getQuoteDocumentPDF(quotePdfRequest: QuotePdfServiceRequest, urlBranch: string): Promise<QuotePdfServiceResponse> {

    try {
      const response = await customHttpClientDocuments.post<QuotePdfServiceResponse>(urlBranch, quotePdfRequest );
      return response.data;

    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postInsertLegalRepresentatives(
    quotationRequest: LegalRepresentative[], quotationNumber: string, 
  ): Promise<number> {
    const url = 'insertarRepresentantesLegales';
    const userInfo = getUserInfo();
    const body: PostInsertRepresentativeLegal = {
      base: {
        peNit1: userInfo.nit1!,
        peNivc: userInfo.nivc!,
        peSucu: userInfo.sucursal,
        peEmpr: userInfo.empresa,
        peNiv1: userInfo.niv1!,
        peNivt: userInfo.nivt!,
      },
      token: quotationNumber,
      repLegales: quotationRequest,
    };

    try {
      const response = await customHttpClientBase.post<LoadQuotationToRenewResponse>(url, body);

      return response.status;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getCoveragesPerPlanRisk(
    branch: string,
    product: string,
    risk: string,
    activityCode: string
  ): Promise<AxiosResponse<GetCoveragesPerPlanServiceResponse, any>> {
    const url = 'coberturasPorPlanRiesgo';
    const params = {
      codigoMoneda: CurrencyCode.PESOS_ARGENTINOS,
      rama: branch,
      producto: product,
      riesgo: risk,
      codigoActividad: activityCode
    };

    try {
      const response =
        await customHttpClient.get<GetCoveragesPerPlanServiceResponse>(url, {
          params,
        });

      if (response.status === 200) {
        response.data.coberturas.map((el: CoveragePerPlan) => {
          el.id = el.id || `${el.codigo}-${el.codigoRiesgo}`;
          return el;
        });
      }

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getListTextSubcategory(
    requestParams: RequestListTextSubcategory
  ): Promise<AxiosResponse<GetListTextSubcategoryResponse, any>> {
    const url = 'listaTextoDeSubcategoria';
    const params = {
      empresa:getUserInfo().empresa,
      sucursal:getUserInfo().sucursal,
      ...requestParams,
    };

    try {
      //const response =  customHttpClientBase.get<GetListTextSubcategoryResponse>(url, { params });

      return {
        data: {} as GetListTextSubcategoryResponse,
        status: 200,
        statusText: "OK",
        headers: {},
        config: {
          headers: {}
        } as InternalAxiosRequestConfig
      };

    } catch (error) {
      throw new Error(getError(error));
    }
  },
  async postInsertTransferRights(
    transferRights: TransferRightsRequest
  ): Promise<AxiosResponse<any, any>> {
    const url = 'insertarTransfDerechos';
    const params = transferRights;

    try {
      const response = await customHttpClientBase.post<any>(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
};

export default QuotationService;
